import {
  Card,
  Title,
  Text,
  Button,
  Group,
  List,
  ThemeIcon,
} from "@mantine/core";
import {
  IconShieldLock,
  IconCircleCheck,
  IconArrowLeft,
} from "@tabler/icons-react";
import { useNavigate } from "react-router";
import { useTranslation } from "../../hooks/useTranslation";
import useTransientStore from "../../data/transcientStore";

interface AccessDeniedProps {
  errorDescription?: string | null;
  correlationId?: string | null;
}

export default function AccessDenied({
  errorDescription,
  correlationId,
}: AccessDeniedProps) {
  const navigate = useNavigate();

  const selectedAddressSystem = useTransientStore(
    (state) => state.selectedAddressSystem
  );
  const addrIdFromStore = selectedAddressSystem?.id || 0;

  const { getTranslation } = useTranslation({
    pageId: 314,
    addressSystemId: addrIdFromStore || 0,
    autoLoad: true,
  });

  return (
    <div
      style={{
        minHeight: "100vh",
        width: "100vw",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background:
          "linear-gradient(135deg,#002f4d 0%, #005b91 60%, #007ab8 100%)",
        padding: "32px",
      }}
    >
      <Card
        shadow="xl"
        radius="lg"
        p="xl"
        style={{
          maxWidth: 600,
          width: "100%",
          background: "rgba(255,255,255,0.94)",
          backdropFilter: "blur(6px)",
        }}
      >
        <Group align="flex-start" mb="md">
          <ThemeIcon size={52} radius="xl" color="red" variant="filled">
            <IconShieldLock size={34} />
          </ThemeIcon>
          <Title order={2} style={{ fontWeight: 700, lineHeight: 1.2 }}>
            {getTranslation("LABEL_ACCESS_RESTRICTED") ||
              "Access Restricted"}
          </Title>
        </Group>
        <Text mb="sm" size="sm" c="dimmed">
          {getTranslation("LABEL_AUTH_SUCCESS_NO_ACCESS") ||
            "You successfully authenticated with Microsoft, but this account is not assigned access to RIMS."}
        </Text>
        <Text mb="md" size="sm">
          {getTranslation("LABEL_GROUP_BASED_ASSIGNMENT_PREFIX") ||
            "The application is configured for"}{" "}
          <strong>
            {getTranslation("LABEL_GROUP_BASED_ASSIGNMENT_STRONG") ||
              "group-based / direct assignment"}
          </strong>
          {getTranslation("LABEL_GROUP_BASED_ASSIGNMENT_SUFFIX") ||
            ". Only users explicitly added by an administrator can proceed."}
        </Text>
        <List
          spacing="xs"
          size="sm"
          mb="md"
          icon={<IconCircleCheck size={16} />}
        >
          <List.Item>
            {getTranslation("LABEL_ACCESS_DENIED_ADMIN_ADD") ||
              "Ask your administrator to add you to the approved Azure AD group (or assign the app directly)."}
          </List.Item>
          <List.Item>
            {getTranslation("LABEL_ACCESS_DENIED_PROVIDE_EMAIL") ||
              "Provide your email:"}{" "}
            <strong>
              {errorDescription?.match(/[\w.+-]+@[\w.-]+/g)?.[0] ||
                (getTranslation("LABEL_ACCESS_DENIED_EMAIL_PLACEHOLDER") ||
                  "your-email")}{" "}
            </strong>{" "}
            {getTranslation("LABEL_ACCESS_DENIED_AND_MESSAGE_BELOW") ||
              "and the message below."}
          </List.Item>
          <List.Item>
            {getTranslation("LABEL_ACCESS_DENIED_SIGN_OUT_TRY_AGAIN") ||
              "Once added, sign out (if partially signed in) and try again."}
          </List.Item>
        </List>
        {errorDescription && (
          <Card
            withBorder
            p="sm"
            mb="md"
            style={{ fontSize: 12, background: "#f8fbfe" }}
          >
            <Text fw={500} size="xs" mb={4}>
              {getTranslation("LABEL_AZURE_AD_RESPONSE") ||
                "Azure AD Response"}
            </Text>
            <Text size="xs" style={{ whiteSpace: "pre-wrap" }}>
              {errorDescription}
            </Text>
            {correlationId && (
              <Text size="xs" mt={4} c="dimmed">
                {getTranslation("LABEL_CORRELATION_ID") ||
                  "Correlation Id:"}{" "}
                {correlationId}
              </Text>
            )}
          </Card>
        )}
        <Group justify="space-between" mt="xs">
          <Button
            variant="outline"
            leftSection={<IconArrowLeft size={16} />}
            onClick={() => navigate("/")}
          >
            {getTranslation("LABEL_BACK_TO_LOGIN") || "Back to Login"}
          </Button>
          <Button color="blue" onClick={() => navigate(0)}>
            {getTranslation("LABEL_RETRY_SIGN_IN") || "Retry Sign In"}
          </Button>
        </Group>
        <Text size="xs" mt="lg" c="dimmed">
          {getTranslation("LABEL_ACCESS_DENIED_CONTACT_SUPPORT") ||
            "If this keeps happening, contact support and reference the correlation id above."}
        </Text>
      </Card>
    </div>
  );
}
